// ─────────────────────────────────────────────────────────────────────────────
// dashboardBrief — extra, optional data for the v2 dashboard's added widgets
// ("To do & deadlines", "GST for last month", "Who owes you") and the summary line.
// Existing read-only endpoints only; every call is permission-gated and may fail silently:
//   GET /api/dashboard/home-summary (previous month — payslips)            [dashboard]
//   GET /api/financials/accounts-receivable (ageing + debtors)             [reports]
//   GET /api/gst-reports/gstr3b?from&to (last month's net payable)         [gst, needs company GSTIN]
//   GET /api/irn/credentials + /api/invoices/fetchallinvoices (B2B w/o IRN, only if e-invoicing is set up)
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from "react";
import API_BASE from "../apiConfig";
import { sendPaymentReminderViaWhatsApp } from "../utils/whatsapp";

const auth = () => ({ "auth-token": localStorage.getItem("token") });
const get = async (path) => { const r = await fetch(`${API_BASE}${path}`, { headers: auth() }); if (!r.ok) throw new Error(String(r.status)); return r.json(); };
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const daysUntil = (d) => Math.ceil((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - new Date(new Date().toDateString())) / 864e5);
export const OVERDUE_DAYS = 30; // standard payment terms (invoice default paymentTimeline)

export function useBrief({ user, hasPermission, summary }) {
  const can = (m) => hasPermission(m, "read");
  const [x, setX] = useState(null);
  const gstin = user?.companyGSTIN;
  useEffect(() => {
    let live = true;
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    const jobs = {
      prev: get(`/api/dashboard/home-summary?month=${prev.getMonth() + 1}&year=${prev.getFullYear()}&mode=monthly`),
      ar: can("reports") ? get("/api/financials/accounts-receivable") : null,
      gst: can("gst") && gstin ? get(`/api/gst-reports/gstr3b?from=${ymd(prev)}&to=${ymd(prevEnd)}`) : null,
      irn: can("invoices") && gstin ? get("/api/irn/credentials").then(async (c) => {
        if (!c?.configured) return null;
        const list = await get("/api/invoices/fetchallinvoices?page=1&limit=100&status=ACTIVE");
        const rows = Array.isArray(list) ? list : list.invoices || [];
        const since = Date.now() - 30 * 864e5;
        return rows.filter((i) => i.clientGSTIN && i.irnStatus !== "generated" && new Date(i.invoiceDate).getTime() >= since).length;
      }) : null,
    };
    const keys = Object.keys(jobs);
    Promise.allSettled(keys.map((k) => jobs[k])).then((res) => {
      if (live) setX(Object.fromEntries(keys.map((k, i) => [k, res[i].status === "fulfilled" ? res[i].value : null])));
    });
    return () => { live = false; };
  }, [gstin]); // eslint-disable-line

  return useMemo(() => {
    const now = new Date();
    const prevName = MONTHS[(now.getMonth() + 11) % 12];
    const k = summary?.kpi || {};
    // Who owes you — full AR when permitted, else the 15 oldest dues from the summary
    let owed = null;
    if (x?.ar) {
      const clients = (x.ar.clients || []).map((c) => {
        const inv = c.invoices || [];
        const od = inv.filter((i) => i.daysOld > OVERDUE_DAYS);
        return { name: c.clientName, total: c.total, count: inv.length, oldest: Math.max(0, ...inv.map((i) => i.daysOld)),
          overdue: od.reduce((a, i) => a + i.outstanding, 0), overdueCount: od.length, oldestInv: [...inv].sort((a, b) => b.daysOld - a.daysOld)[0] };
      });
      owed = { total: x.ar.totalOutstanding || 0, over90: x.ar.aging?.days90plus || 0, aging: x.ar.aging, clients };
    } else if (summary) {
      const by = {};
      (summary.clientDues || []).forEach((i) => {
        const days = Math.floor((Date.now() - new Date(i.invoiceDate)) / 864e5), due = (i.grandTotal || 0) - (i.amountPaid || 0);
        const c = (by[i.clientName] = by[i.clientName] || { name: i.clientName, total: 0, count: 0, oldest: 0, overdue: 0, overdueCount: 0, oldestInv: null });
        c.total += due; c.count += 1;
        if (days > c.oldest) { c.oldest = days; c.oldestInv = { invoiceNumber: i.invoiceNumber }; }
        if (days > (i.paymentTimeline ?? OVERDUE_DAYS)) { c.overdue += due; c.overdueCount += 1; }
      });
      owed = { total: k.receivables?.amount || 0, over90: null, aging: null, clients: Object.values(by).sort((a, b) => b.total - a.total), partial: true };
    }
    if (owed) {
      owed.overdue = owed.clients.reduce((s, c) => s + c.overdue, 0);
      owed.overdueCount = owed.clients.reduce((s, c) => s + c.overdueCount, 0);
      owed.overdueClients = owed.clients.filter((c) => c.overdueCount).length;
      owed.oldest = Math.max(0, ...owed.clients.map((c) => c.oldest));
    }

    // To do & deadlines (supplier dues, stock and recurring stay in "Needs attention")
    const todo = [];
    if (can("invoices") && owed?.overdueCount > 0) todo.push({ id: "overdue", tone: "bad", glyph: "clock", title: `${owed.overdueCount}${owed.partial && owed.overdueCount >= 15 ? "+" : ""} overdue invoice${owed.overdueCount === 1 ? "" : "s"}`, sub: `${owed.overdueClients} client${owed.overdueClients === 1 ? "" : "s"} · oldest ${owed.oldest} days`, amount: owed.overdue, to: "/dashboard/viewinvoices" });
    const due1 = new Date(now.getFullYear(), now.getMonth(), 11), due3 = new Date(now.getFullYear(), now.getMonth(), 20);
    if (x?.gst) {
      const pay = Math.max(0, x.gst.netPayable?.total || 0);
      const when = (d) => (daysUntil(d) === 0 ? "Due today" : `Due ${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)} · in ${daysUntil(d)} day${daysUntil(d) === 1 ? "" : "s"}`);
      if (daysUntil(due1) >= 0) todo.push({ id: "gstr1", tone: daysUntil(due1) <= 3 ? "bad" : "warn", glyph: "gst", title: `GSTR-1 for ${prevName}`, sub: when(due1), to: "/dashboard/gst-reports" });
      if (daysUntil(due3) >= 0) todo.push({ id: "gstr3b", tone: daysUntil(due3) <= 3 ? "bad" : "warn", glyph: "gst", title: `GSTR-3B for ${prevName}`, sub: when(due3), amount: pay || null, amountNote: pay ? "to pay" : "nil", to: "/dashboard/gst-reports" });
    }
    if (x?.irn > 0) todo.push({ id: "irn", tone: "warn", glyph: "invoice", title: `${x.irn} B2B invoice${x.irn === 1 ? "" : "s"} without an IRN`, sub: "Issued in the last 30 days", to: "/dashboard/viewinvoices" });
    if (can("bills") && (k.payables?.billsAmount || 0) > 0) todo.push({ id: "bills", tone: "warn", glyph: "wallet", title: "Unpaid bills", sub: "Rent, utilities and other bills", amount: k.payables.billsAmount, to: "/dashboard/bills" });
    const pp = x?.prev?.payroll;
    if (can("payroll") && pp?.total > 0) {
      if (!pp.paidAmount && !pp.pendingAmount) todo.push({ id: "payroll", tone: "warn", glyph: "people", title: `${prevName} payslips not generated`, sub: `${pp.total} active staff`, to: "/dashboard/staff/payroll" });
      else if (pp.pendingAmount > 0) todo.push({ id: "payroll", tone: "warn", glyph: "people", title: `${prevName} salaries unpaid`, sub: `${pp.total - pp.paid} of ${pp.total} staff`, amount: pp.pendingAmount, to: "/dashboard/staff/payroll" });
    }
    const rank = { bad: 0, warn: 1, info: 2 };
    todo.sort((a, b) => rank[a.tone] - rank[b.tone]);

    return { loaded: !!x, todo, owed, gst: x?.gst || null, prevName, dueDates: { gstr1: due1, gstr3b: due3 } };
  }, [x, summary]); // eslint-disable-line
}

// WhatsApp reminder for a debtor's oldest open invoice (needs publicId + client phone from the full invoice).
export async function remindClient(c, { onError }) {
  if (!c.oldestInv?.invoiceNumber) throw new Error("No open invoice found");
  const hits = await get(`/api/invoices/search?q=${encodeURIComponent(c.oldestInv.invoiceNumber)}`);
  const hit = (Array.isArray(hits) ? hits : []).find((i) => i.invoiceNumber === c.oldestInv.invoiceNumber);
  if (!hit?._id) throw new Error("Couldn't find that invoice");
  const inv = await get(`/api/invoices/${hit._id}`);
  const phone = inv.clientId?.clientPhone;
  if (!phone) { onError?.(`No phone number saved for ${c.name}. Add it in Clients.`); return; }
  sendPaymentReminderViaWhatsApp(inv, String(phone), c.name);
}
